import { memo, useMemo } from "react";
import { useSelector } from "react-redux";
import { Grid, Tooltip, useTheme } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ScheduleIcon from "@mui/icons-material/Schedule";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { IAircraft } from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { getMaintenanceItemCounts, IMaintenanceItemCounts } from "./AircraftMaintenanceUtils";

interface IAircraftMaintenanceCountersProps {
  aircraftId: string;
}

/**
 * Displays compact maintenance summary badges (overdue, due soon, deferred)
 * for a single aircraft row in the Flight Timeline.
 * Memoized to avoid unnecessary re-renders when the aircraft list is large.
 */
export const AircraftMaintenanceCounters = memo(
  ({ aircraftId }: IAircraftMaintenanceCountersProps): JSX.Element | null => {
    const theme = useTheme();

    const aircraft = useSelector((state: IRootState) =>
      state.structures.customerStructure.aircraft?.find((ac: IAircraft) => ac._id === aircraftId)
    );

    const mxSummary = useMemo(
      (): IMaintenanceItemCounts => getMaintenanceItemCounts(aircraft?.maintenance),
      [aircraft?.maintenance]
    );

    const hasAny = mxSummary.overdue + mxSummary.dueSoon + mxSummary.deferred > 0;
    if (!hasAny) {
      return <Grid item className="aircraft_maintenance_counters" />;
    }

    return (
      <Grid item className="aircraft_maintenance_counters">
        {mxSummary.overdue > 0 && (
          <Tooltip title={`Overdue: ${mxSummary.overdue}`} placement="top" arrow>
            <div className="mx_badge">
              <ErrorOutlineIcon className="mx_badge_icon" sx={{ color: "error.main", mb: 0.5 }} />
              <span className="mx_badge_count" style={{ color: theme.palette.error.main }}>
                {mxSummary.overdue}
              </span>
            </div>
          </Tooltip>
        )}
        {mxSummary.dueSoon > 0 && (
          <Tooltip title={`Due Soon: ${mxSummary.dueSoon}`} placement="top" arrow>
            <div className="mx_badge">
              <ScheduleIcon className="mx_badge_icon" sx={{ color: "info.main", mb: 0.5 }} />
              <span className="mx_badge_count" style={{ color: theme.palette.info.main }}>
                {mxSummary.dueSoon}
              </span>
            </div>
          </Tooltip>
        )}
        {mxSummary.deferred > 0 && (
          <Tooltip title={`Deferred: ${mxSummary.deferred}`} placement="top" arrow>
            <div className="mx_badge">
              <WarningAmberIcon className="mx_badge_icon" sx={{ color: "warning.main", mb: 0.5 }} />
              <span className="mx_badge_count" style={{ color: theme.palette.warning.main }}>
                {mxSummary.deferred}
              </span>
            </div>
          </Tooltip>
        )}
      </Grid>
    );
  }
);

AircraftMaintenanceCounters.displayName = "AircraftMaintenanceCounters";
